import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { listJobs } from '@/api/jobs';
import { getReviewQueue, getReviewQueueAll } from '@/api/review';
import { Card } from '@/components/ui/card/card';
import { StatusTag } from '@/components/ui/status-tag/status-tag';

import styles from './review-queue.module.css';

export function ReviewQueuePage() {
  const navigate = useNavigate();
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);

  const { data: jobsData } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => listJobs(),
  });

  // 学习通式批阅中心：默认加载全部待批阅（跨作业），无需先选作业
  const { data: queueAll } = useQuery({
    queryKey: ['reviewQueueAll'],
    queryFn: () => getReviewQueueAll(),
  });

  const { data: queueByJob } = useQuery({
    queryKey: ['reviewQueue', selectedJobId],
    queryFn: () => getReviewQueue(selectedJobId!),
    enabled: !!selectedJobId,
  });

  const queue = selectedJobId ? queueByJob : queueAll;
  const pendingCount = queue?.progress.pending ?? 0;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>批阅中心</h1>
          <p className={styles.subtitle}>
            {pendingCount > 0
              ? `当前有 ${pendingCount} 份提交待批阅`
              : '暂无待批阅的提交'}
          </p>
        </div>
        <select
          className={styles.select}
          value={selectedJobId ?? ''}
          onChange={(e) => setSelectedJobId(Number(e.target.value) || null)}
        >
          <option value="">全部作业</option>
          {jobsData?.items.map((job) => (
            <option key={job.id} value={job.id}>
              {job.title}
            </option>
          ))}
        </select>
      </header>

      <div className={styles.layout}>
        <Card className={styles.listCard}>
          <h2 className={styles.sectionTitle}>
            待批阅列表
            {queue?.progress && (
              <span className={styles.progress}>
                已批 {queue.progress.reviewed}/{queue.progress.total}
              </span>
            )}
          </h2>
          <div className={styles.list}>
            {queue?.items.map((item) => (
              <div
                key={item.submission_id}
                className={styles.item}
                onClick={() => navigate(`/review/${item.submission_id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(`/review/${item.submission_id}`);
                }}
              >
                <div className={styles.itemHeader}>
                  <span className={styles.student}>{item.student_name}</span>
                  <StatusTag status={item.status} />
                </div>
                <div className={styles.itemMeta}>
                  {item.job_title && <span className={styles.jobTitle}>{item.job_title}</span>}
                  <span>得分：{item.score ?? '-'}</span>
                  <span>置信度：{item.confidence ?? '-'}</span>
                </div>
              </div>
            ))}
            {queue && queue.items.length === 0 && (
              <div className={styles.empty}>暂无待批阅提交</div>
            )}
          </div>
        </Card>

        <Card className={styles.hintCard}>
          <h2 className={styles.sectionTitle}>批阅提示</h2>
          <p className={styles.hint}>
            列表默认展示全部作业的提交，待批阅优先排序；可通过右上角下拉按作业筛选。
            点击条目进入批阅详情，可确认 AI 评分、修改分数或打回让学生重交。
          </p>
          <p className={styles.hint}>
            学生重新提交后，旧评分会自动作废并进入待批阅队列。
          </p>
        </Card>
      </div>
    </div>
  );
}
